// `LeadsTablePage` — EPIC-11 entry surface (US-139..US-152, US-153 banner).
//
// Composition:
//   - PageHeader — title + subtitle.
//   - Toolbar    — search input (240px) + Compte filter + Business filter +
//                  spacer + counter + CSV download.
//   - Capture banner (US-153) — reactive realtime banner.
//   - BulkActions bar (US-145, US-146, US-148) — appears when ≥1 selected.
//   - Table — DS `LeadRow` composite per row + sticky header + select-all.
//   - Empty states — distinct copy for "no leads yet" vs "filter mismatch".
//   - LeadDetailDrawer (US-143, 144, 147) — rendered as overlay.
//   - CRMExportModal (US-148, 149, 150, 151) — modal.

import { useMemo, useState } from 'react';

import {
  useBusinesses,
  useInfiniteLeads,
  useLinkedinAccounts,
  type LeadRow as ApiLead,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  IconButton,
  Input,
  LeadRow,
  PageHeader,
  Pill,
  type LeadRowData,
} from '@barel/ui';
import { ArrowUpRight, Search } from 'lucide-react';

import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { BulkActions } from './BulkActions';
import { CRMExportModal } from './CRMExportModal';
import { CaptureFromExtBanner } from './CaptureFromExtBanner';
import { CSVDownload } from './CSVDownload';
import { DedupeBadge } from './DedupeBadge';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { StatusInlineEditor } from './StatusInlineEditor';
import {
  formatLastActivity,
  toRowData,
  type AccountLite,
  type BusinessLite,
} from './lib/leadAdapters';

const ALL = 'all';
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

export function LeadsTablePage() {
  const t = useT('leads');

  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState<string>(ALL);
  const [businessFilter, setBusinessFilter] = useState<string>(ALL);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailLead, setDetailLead] = useState<ApiLead | null>(null);
  const [crmModalLeads, setCrmModalLeads] = useState<ApiLead[] | null>(null);

  // US-142: each keystroke updates `search` so the input stays responsive,
  // but the query key only flips after the user stops typing for ~250ms.
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const accountsQ = useLinkedinAccounts();
  const businessesQ = useBusinesses();
  const leadsFilter = useMemo(
    () => ({
      account: accountFilter === ALL ? undefined : accountFilter,
      business: businessFilter === ALL ? undefined : businessFilter,
      q: debouncedSearch.trim() || undefined,
    }),
    [accountFilter, businessFilter, debouncedSearch],
  );
  const leadsQ = useInfiniteLeads(leadsFilter, { limit: PAGE_SIZE });

  const accounts: AccountLite[] = useMemo(
    () =>
      (accountsQ.data?.accounts ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        color: a.color,
      })),
    [accountsQ.data],
  );
  const businesses: BusinessLite[] = useMemo(
    () =>
      (businessesQ.data?.businesses ?? []).map((b) => ({
        id: b.id,
        name: b.name,
      })),
    [businessesQ.data],
  );

  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const businessById = useMemo(() => new Map(businesses.map((b) => [b.id, b])), [businesses]);

  const leads = useMemo(() => {
    const pages = leadsQ.data?.pages ?? [];
    const out: ApiLead[] = [];
    for (const page of pages) out.push(...page.items);
    return out;
  }, [leadsQ.data]);

  // Detect duplicate URN/slug across the result set so we can light up
  // `<DedupeBadge>` on the second+ occurrence (US-154 client side).
  const dupKeySeen = useMemo(() => {
    const out = new Map<string, number>();
    for (const lead of leads) {
      const key = lead.prospectLinkedinUrn ?? lead.prospectVanitySlug;
      if (!key) continue;
      out.set(key, (out.get(key) ?? 0) + 1);
    }
    return out;
  }, [leads]);

  // Selection helpers.
  const allChecked = leads.length > 0 && leads.every((l) => selected.has(l.id));
  const someChecked = !allChecked && leads.some((l) => selected.has(l.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };
  const toggleOne = (id: string, next: boolean) => {
    setSelected((prev) => {
      const out = new Set(prev);
      if (next) out.add(id);
      else out.delete(id);
      return out;
    });
  };

  const selectedLeads = useMemo(() => leads.filter((l) => selected.has(l.id)), [leads, selected]);

  const openCrm = (subset: ApiLead[]) => {
    if (subset.length === 0) return;
    setCrmModalLeads(subset);
  };

  // Render helpers.
  const accountOptions = [
    { id: ALL, label: t('filter.allAccounts') },
    ...accounts.map((a) => ({ id: a.id, label: a.name })),
  ];
  const businessOptions = [
    { id: ALL, label: t('filter.allBusinesses') },
    ...businesses.map((b) => ({ id: b.id, label: b.name })),
  ];

  const isEmpty = !leadsQ.isLoading && !leadsQ.isError && leads.length === 0;
  // Distinguish "true empty" from "filter mismatch" — true empty when the
  // filters are at default values + search is empty.
  const filtersActive = accountFilter !== ALL || businessFilter !== ALL || search.trim().length > 0;

  return (
    <section className="flex flex-col gap-4" data-testid="leads-page">
      <PageHeader title={t('list.title')} subtitle={t('list.subtitle')} />

      <CaptureFromExtBanner
        onView={(leadId) => {
          const target = leads.find((l) => l.id === leadId);
          if (target) setDetailLead(target);
        }}
      />

      {/* Toolbar */}
      <div
        className="flex flex-wrap items-center gap-2 border-b border-default bg-bg-surface px-5 py-3"
        data-testid="leads-toolbar"
      >
        <div className="relative w-[240px]">
          <Search
            size={14}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-tertiary"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            data-testid="leads-search"
            aria-label={t('list.searchLabel')}
            style={{ paddingLeft: '32px' }}
          />
        </div>

        <FilterDropdown
          label={t('filter.account')}
          options={accountOptions}
          value={accountFilter}
          onChange={setAccountFilter}
          testId="leads-filter-account"
        />
        <FilterDropdown
          label={t('filter.business')}
          options={businessOptions}
          value={businessFilter}
          onChange={setBusinessFilter}
          testId="leads-filter-business"
        />

        <div className="flex-1" />

        <CSVDownload filter={leadsFilter} count={leads.length} variant="text" />

        <span className="text-xs text-tertiary" data-testid="leads-counter">
          {t('list.counter', { count: leads.length })}
        </span>
      </div>

      <BulkActions
        selectedIds={Array.from(selected)}
        onClearSelection={() => setSelected(new Set())}
        onOpenCrm={() => openCrm(selectedLeads)}
      />

      {/* Body */}
      {leadsQ.isLoading ? (
        <Card padding="lg" radius="2xl" data-testid="leads-loading">
          <p className="text-center text-sm text-secondary">{t('list.loading')}</p>
        </Card>
      ) : null}

      {leadsQ.isError ? (
        <Card padding="lg" radius="2xl" data-testid="leads-error">
          <Banner tone="error" title={t('list.errorTitle')}>
            {t('list.errorBody')}
          </Banner>
        </Card>
      ) : null}

      {isEmpty && !filtersActive ? (
        <Card padding="lg" radius="2xl" data-testid="leads-empty">
          <EmptyState title={t('empty.trueTitle')} description={t('empty.trueDescription')} />
        </Card>
      ) : null}

      {isEmpty && filtersActive ? (
        <Card padding="lg" radius="2xl" data-testid="leads-empty-filtered">
          <EmptyState
            title={t('empty.filteredTitle')}
            description={t('empty.filteredDescription')}
          />
        </Card>
      ) : null}

      {!isEmpty && !leadsQ.isLoading && !leadsQ.isError ? (
        <div
          role="table"
          aria-label={t('list.title')}
          data-testid="leads-table-wrapper"
          className="overflow-auto"
        >
          <div
            role="rowgroup"
            className="sticky top-0 z-10 bg-bg-surface"
            data-testid="leads-thead"
          >
            <div role="row" className="flex items-center border-b border-strong px-3 py-2">
              <div role="columnheader" className="px-2" style={{ width: 36, paddingLeft: 20 }}>
                <Checkbox
                  aria-label={t('table.selectAll')}
                  checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                  onCheckedChange={toggleAll}
                  data-testid="leads-select-all"
                  size="sm"
                />
              </div>
              {[
                { id: 'lead', label: t('table.lead'), width: 260 },
                { id: 'company', label: t('table.company'), width: 180 },
                { id: 'account', label: t('table.account'), width: 110 },
                { id: 'business', label: t('table.business'), width: 130 },
                { id: 'lastAction', label: t('table.lastAction'), width: 320 },
                { id: 'date', label: t('table.date'), width: 80 },
                { id: 'status', label: t('table.status'), width: 130 },
              ].map((col) => (
                <div
                  key={col.id}
                  role="columnheader"
                  className="px-3 text-left text-2xs font-bold uppercase tracking-wider text-tertiary"
                  style={{ width: col.width }}
                >
                  {col.label}
                </div>
              ))}
            </div>
          </div>
          <div role="rowgroup" data-testid="leads-rows">
            {leads.map((lead) => {
              const account =
                typeof lead.linkedinAccountId === 'string'
                  ? accountById.get(lead.linkedinAccountId)
                  : undefined;
              const business =
                typeof lead.businessId === 'string' ? businessById.get(lead.businessId) : undefined;
              const lastActivity = formatLastActivity(
                lead.lastActivityAt,
                'fr-FR',
                t('detail.today'),
                t('detail.yesterday'),
              );
              const data: LeadRowData = toRowData(lead, account, business, lastActivity.iso);
              const dupKey = lead.prospectLinkedinUrn ?? lead.prospectVanitySlug;
              const isDup = dupKey ? (dupKeySeen.get(dupKey) ?? 0) > 1 : false;
              return (
                <div key={lead.id} className="flex items-stretch" data-lead-id={lead.id}>
                  <div className="flex-1 min-w-0">
                    <LeadRow
                      as="div"
                      lead={data}
                      selected={selected.has(lead.id)}
                      active={detailLead?.id === lead.id}
                      labels={{
                        checkbox: t('table.checkbox', { name: lead.prospectName }),
                        sendToCrm: t('table.sendToCrmLabel'),
                        status: t(`status.${lead.status}`),
                        source: t(`source.${lead.sourcePill.toLowerCase()}`),
                        lastActivity: lastActivity.label,
                      }}
                      onToggleSelect={(next) => toggleOne(lead.id, next)}
                      onSelect={() => setDetailLead(lead)}
                      onSendToCrm={() => openCrm([lead])}
                      data-testid={`leads-row-${lead.id}`}
                    />
                  </div>
                  {/* Wave C1 (US-152) — inline status pill. Renders the
                      `<StatusInlineEditor>` per row so the user can change a
                      lead's status without opening the detail drawer. The
                      DS `LeadRow` composite never owned a status cell of its
                      own, so we host the editor as a sibling of the row.
                      Click bubbling is stopped inside the editor. */}
                  <div
                    className="flex items-center px-3"
                    data-testid={`leads-row-${lead.id}-status`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <StatusInlineEditor lead={lead} />
                  </div>
                  {isDup || lead.crmSync ? (
                    <div
                      className="flex items-center gap-2 px-3"
                      data-testid={`leads-row-${lead.id}-flags`}
                    >
                      {isDup ? <DedupeBadge flagged /> : null}
                      {lead.crmSync ? (
                        <Pill tone="success" variant="soft" size="xs">
                          {t('row.crmSyncedShort')}
                        </Pill>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {leadsQ.hasNextPage ? (
            <div className="flex justify-center py-4" data-testid="leads-load-more-row">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  void leadsQ.fetchNextPage();
                }}
                disabled={leadsQ.isFetchingNextPage}
                data-testid="leads-load-more"
              >
                {leadsQ.isFetchingNextPage ? t('list.loading') : t('list.loadMore')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <LeadDetailDrawer
        open={detailLead !== null}
        onOpenChange={(next) => {
          if (!next) setDetailLead(null);
        }}
        lead={detailLead}
        accounts={accounts}
        businesses={businesses}
        onSendToCrm={(lead) => openCrm([lead])}
      />

      {crmModalLeads ? (
        <CRMExportModal
          open
          onOpenChange={(next) => {
            if (!next) setCrmModalLeads(null);
          }}
          leads={crmModalLeads}
          accounts={accounts}
          businesses={businesses}
          onCompleted={() => {
            setSelected(new Set());
            setCrmModalLeads(null);
          }}
        />
      ) : null}
    </section>
  );
}

interface FilterOption {
  id: string;
  label: string;
}

interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (next: string) => void;
  testId?: string;
}

function FilterDropdown({ label, options, value, onChange, testId }: FilterDropdownProps) {
  // Lightweight native-select dropdown — keeps the toolbar dense and
  // accessible without re-implementing the Radix popover dance for two
  // straightforward filter chips.
  const current = options.find((o) => o.id === value) ?? options[0];
  const selected = value !== ALL;
  return (
    <label
      data-testid={testId}
      data-selected={selected || undefined}
      className="inline-flex items-center gap-2 rounded-pill border border-default bg-bg-elevated px-3 py-1 text-xs font-medium"
    >
      <span className="text-tertiary">{label}:</span>
      <span className="sr-only">{current?.label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer bg-transparent text-primary outline-none"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// Re-export the per-row "send to CRM" icon for downstream stories.
export { ArrowUpRight as RowSendIcon, IconButton };
